import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { type CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import { ButtonItem, MenuGroup } from '@atlaskit/menu';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Popup } from '@atlaskit/popup';

import { ANALYTICS_CHANNEL } from '../../constants';
import type { GlobalShortcutActionsItem } from '../../types';

import { messages } from './messages';

const fireShortcutActionMenuClickedAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'ShortcutActionMenu',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
};

type ShortcutActionProps = {
	actions: GlobalShortcutActionsItem[];
	analyticsName?: string;
};
const ShortcutActionMenuDeprecated = ({ actions }: ShortcutActionProps) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<MenuGroup>
					{actions.map((action, index) => (
						<ButtonItem
							key={index}
							onClick={(e) => {
								e.preventDefault();
								action.onClick?.();
								setIsOpen(false);
							}}
						>
							{action.component}
						</ButtonItem>
					))}
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					spacing="compact"
					icon={(props) => <AddIcon {...props} size="small" />}
					label={formatMessage(messages.shortcutActionOpenMenuLabel)}
					onClick={() => setIsOpen(true)}
				/>
			)}
		/>
	);
};

const ShortcutActionMenuUpdated = ({ actions, analyticsName }: ShortcutActionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);

	const handleClick = useCallback(() => {
		setIsOpen(true);
		fireShortcutActionMenuClickedAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [createAnalyticsEvent, analyticsName, setIsOpen]);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<MenuGroup>
					{actions.map((action, index) => (
						<ButtonItem
							key={index}
							onClick={(e) => {
								e.preventDefault();
								action.onClick?.();
								setIsOpen(false);
							}}
						>
							{action.component}
						</ButtonItem>
					))}
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					spacing="compact"
					icon={(props) => <AddIcon {...props} size="small" />}
					label={formatMessage(messages.shortcutActionOpenMenuLabel)}
					onClick={handleClick}
				/>
			)}
		/>
	);
};

export const ShortcutActionMenu = componentWithFG(
	'global_shortcuts_add_actions_click_event',
	ShortcutActionMenuUpdated,
	ShortcutActionMenuDeprecated,
);
